import {useEffect, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';

import LoadingIndicator from '../../../components/loading-indicator';
import {AppContent, ContentRow} from '../../../styled/components';
import {ActionsRow, ContactTitleContainer, ContentRowHeader, FormRow} from './contact-title-form.styles';
import {getContactTitle, saveContactTitle} from '../api-service';

const emptyTitle = {
  name: '',
};

function ContactTitleForm({titleId, onCancel, onSaved}) {
  const isEditMode = titleId !== null && titleId !== 'new';
  const [formData, setFormData] = useState(emptyTitle);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(null);

  const title = isEditMode ? `Contact titles / ${formData?.name || ''}` : 'Contact titles / New';
  const isNameValid = formData.name?.trim().length > 0;

  useEffect(() => {
    if (!isEditMode) {
      setFormData(emptyTitle);
      return;
    }
    setLoading(true);
    getContactTitle(titleId)
      .then((data) => {
        if (!data) {
          setServerError(`Contact title ${titleId} not found`);
          return;
        }
        setFormData({...emptyTitle, ...data});
      })
      .catch((err) => setServerError(err?.message || 'Error loading contact title'))
      .finally(() => setLoading(false));
  }, [titleId, isEditMode]);

  const handleFieldChange = (ev) => {
    const {name, value} = ev.target;
    setFormData((prev) => ({...prev, [name]: value}));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!isNameValid) return;
    setLoading(true);
    setServerError(null);
    try {
      const payload = {
        ...(isEditMode ? {id: Number(titleId)} : {}),
        name: formData.name.trim(),
      };
      const saved = await saveContactTitle(payload);
      onSaved?.(saved ?? (isEditMode ? {id: titleId} : undefined));
    } catch (err) {
      setServerError(err?.message || 'Error saving contact title');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingIndicator/>}
      <AppContent>
        <ContentRow sx={ContentRowHeader}>
          <Typography variant="h5" component="h1">{title}</Typography>
          <Box sx={ActionsRow}>
            <Button variant="outlined" color="error" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={loading || !isNameValid}
              onClick={handleSubmit}
            >
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </Box>
        </ContentRow>
        {serverError && (
          <ContentRow>
            <Alert severity="error" onClose={() => setServerError(null)}>{serverError}</Alert>
          </ContentRow>
        )}
        <ContentRow>
          <Box component="form" sx={ContactTitleContainer} onSubmit={handleSubmit} noValidate>
            <Box sx={FormRow}>
              <TextField
                name="name"
                label="Name"
                value={formData.name}
                onChange={handleFieldChange}
                fullWidth
                required
                autoFocus
              />
            </Box>
          </Box>
        </ContentRow>
      </AppContent>
    </>
  );
}

export default ContactTitleForm;
